import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {updatePublishingParamTitle} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import {titleValidations, YoutubeTitleOnScreenMax} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {useAreCaptionsBeingAutoGenerated, useWizardTitleErrors} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {useAppDispatch} from '@/hooks';
import type {SocialAccount} from '@/components/social-media/account.vo';
import styles from './title-input.module.scss';
import {getRandomizedLoadingTitle} from '@Components/social-media-wizard/components/title-input/title-input.types';

interface TitleInputProps {
  account: SocialAccount;
  setTitleError?: (newValue: string | null) => void;
  areURLsAllowed?: boolean;
}

export function TitleInput({
  account,
  setTitleError = (newValue: string | null): void => {},
  areURLsAllowed = titleValidations[account.type].areURLsAllowed,
}: TitleInputProps): ReactElement<any> | null {
  const publishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const [title, setTitle] = useState('');
  const [titleErrorState, setTitleErrorState] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const titleErrors = useWizardTitleErrors();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const [autoCaptionGenerationLoadingTitle, setAutoCaptionGenerationLoadingTitle] = useState<string>(getRandomizedLoadingTitle());

  const dispatch = useAppDispatch();

  useEffect((): void => {
    if (!publishingParamsForAccount) {
      return;
    }

    if ('title' in publishingParamsForAccount) {
      setTitle(publishingParamsForAccount.title);
    }
  }, [areCaptionsBeingAutoGenerated]);

  useEffect((): void => {
    if (titleErrors !== undefined) {
      if (titleErrors[account.id]) {
        handleError();
      } else {
        setTitleError(null);
        setTitleErrorState(false);
      }
    }
  }, [titleErrors]);

  useEffect(() => {
    const randomizedLoadingTitle = getRandomizedLoadingTitle();
    setAutoCaptionGenerationLoadingTitle(randomizedLoadingTitle);
  }, []);

  const handleError = (): void => {
    setTitleError(titleErrors[account.id]);
    setTitleErrorState(true);
  };

  const shortenTitleIfNeeded = (inputStr: string): string => {
    if (inputStr.length <= YoutubeTitleOnScreenMax || account.type !== SocialPlatform.YOUTUBE) {
      return inputStr;
    }

    return `${inputStr.substring(0, YoutubeTitleOnScreenMax)}...`;
  };

  const onTitleAdded = (inputTitle: InputType): void => {
    const strInput = inputTitle as string;

    setTitle(strInput);
    dispatch(
      updatePublishingParamTitle({
        idSocialMediaAccount: account.id,
        title: strInput,
        areURLsAllowed,
      })
    );
  };

  const getEmojiFromLangString = (string?: string): string | undefined => {
    return string?.slice(0, 2);
  };

  const getTextFromLangString = (string?: string): string | undefined => {
    return string?.slice(2);
  };

  const getContent = (): ReactElement => {
    if (areCaptionsBeingAutoGenerated) {
      return (
        <div className={`${styles.title} ${styles.aiLoader}`}>
          <span>{getEmojiFromLangString(autoCaptionGenerationLoadingTitle)}</span>
          <span className={styles.aiFont}>{getTextFromLangString(autoCaptionGenerationLoadingTitle)}</span>
        </div>
      );
    }

    return (
      <InputField
        input={isUserTyping ? title : shortenTitleIfNeeded(title)}
        onInputBlur={() => {
          setIsUserTyping(false);
        }}
        onInputSubmit={() => {
          setIsUserTyping(false);
        }}
        onInputFocus={() => {
          setIsUserTyping(true);
        }}
        onInputChange={onTitleAdded}
        className={`${styles.title} ${titleErrorState ? styles.titleError : null}`}
        inputClassName={styles.noBorder}
        placeholder={window.i18next.t('pmwjs_title')}
      />
    );
  };
  return getContent();
}
